/**
 * Какой отрезок клипа захвата движений играет приём (по его позе): данные, общие для рисования (кадр клипа) и мира
 * (ход корня — src/content/abilities/rootMotion.ts).
 */
/** Отрезок клипа в кадрах атласа (30 к/с): [клип, с, по, кадр удара]. */
export type ClipRange = readonly [string, number, number, number?];

/** Какой отрезок захвата движений играет способность (по её позе). Удар — середина отрезка. */
export const CLIP_RANGES: Readonly<Record<string, ClipRange>> = {
  // кадр удара — где кулак или стопа дальше всего (замер по кадрам атласа, tools/art)
  jab: ["lead-jab", 10, 30, 14],
  hook: ["punch-combo", 20, 34, 26],
  lightKick: ["side-kick", 12, 34, 20],
  heavyKick: ["mma-kick", 12, 40, 22],
  uppercut: ["uppercut", 0, 40, 15],
  crouchJab: ["crouch-jab", 10, 30, 15],
  sweep: ["soccer-tackle", 0, 44, 15],
  legSweep: ["leg-sweep", 8, 46, 32],
  slide: ["running-slide", 2, 40, 8],
  runKnee: ["flying-knee-punch-combo", 2, 26, 10],
  cross: ["punch-combo", 12, 24, 18],
  bodyHook: ["punch-combo", 27, 38, 33],
  finisher: ["uppercut", 0, 40, 15],
  chargePunch: ["punch-combo", 12, 24, 18],
  frontKick: ["kicking", 12, 30, 19],
  hurricane: ["hurricane-kick", 6, 34, 14],
  spinKick: ["roundhouse-kick", 18, 44, 32],
  airPunch: ["flying-knee-punch-combo", 16, 30, 22],
  riseKick: ["inverted-double-kick-to-kip-up", 40, 92, 65],
  elbowLunge: ["elbow-punch", 4, 22, 11],
  overhandElbow: ["illegal-elbow-punch", 8, 50, 23],
  risingElbow: ["elbow-uppercut-combo", 14, 34, 20],
  // кадр удара — нога вытянута (40–42), а не на взлёте (31: нога ещё внизу); замер касания 05.11.2026
  dropKick: ["drop-kick", 20, 46, 41],
  chargeKick: ["mma-kick", 12, 40, 22],
  airKick: ["flying-knee-punch-combo", 14, 40, 22],
  airHeavy: ["flying-knee-punch-combo", 24, 50, 39],
  dashStrike: ["flying-knee-punch-combo", 0, 60, 39],
  flip: ["flip-kick", 0, 60, 30],
  risingStrike: ["uppercut", 4, 40, 15],
  magicShot: ["magic-attack", 16, 62, 39],
  backRun: ["running-to-turn", 0, 46],
  // бросок: отрезок пары клипов 110–230 (60 к/с) = 55–115 атласа, тот же у обоих (sim/throw.ts)
  throwAggressor: ["throw-aggressor", 55, 115],
  grabWhiff: ["throw-aggressor", 40, 62],
  tauntComeOn: ["wide-arm-spell-casting", 0, 106],
  tauntBreak: ["breakdance-1990", 0, 74],
  tauntChicken: ["chicken-dance", 0, 143],
  blink: ["fighting-idle", 0, 20],
  vanish: ["fighting-idle", 0, 20],
};

/**
 * Темп удара (Джон 04.11.2026, 22:50: «много раз жму квадратик и стою, одно и то же движение… медленное»): серия идёт
 * быстро, удар каждые 7–9 кадров мира, а родной темп клипа — кадр клипа на два кадра мира; удар показывал только кусок
 * у попадания и тонул в переходе. Замах — не быстрее WINDUP_SPEED кадров клипа на кадр мира (вдвое быстрее родного),
 * кадр удара — на первый активный кадр мира, проводка — FOLLOW_SPEED (в полтора раза быстрее родного). Общий для
 * рисования и хода корня: стопа и тело идут одним темпом.
 */
export const WINDUP_SPEED = 1;
export const FOLLOW_SPEED = 0.75;

export function strikeClipFrame(range: ClipRange, startup: number, abilityFrame: number): number {
  const impact = range[3] ?? range[1];
  const start = Math.max(range[1], impact - startup * WINDUP_SPEED);
  const frame =
    abilityFrame < startup
      ? start + ((impact - start) * abilityFrame) / Math.max(1, startup)
      : impact + (abilityFrame - startup) * FOLLOW_SPEED;
  return Math.min(range[2] - 1, Math.max(range[1], frame));
}
